/**
 * Dashboard Templates
 * Pre-built dashboard layouts users can start from
 */

import { WIDGET_TYPES, generateWidgetId } from './widgetConfig';

// Template definitions
export const DASHBOARD_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Canvas',
    description: 'Start from scratch with an empty dashboard',
    icon: 'layout',
    layout: [],
    color: 'slate',
  },
  {
    id: 'it_executive',
    name: 'IT Executive Dashboard',
    description: 'Complete IT overview with M365 metrics, Azure costs, security posture, and service health',
    icon: 'shield',
    color: 'blue',
    featured: true,
    layout: [
      // Row 1: M365 Overview Hero Widget (spans 8 columns)
      {
        i: generateWidgetId(WIDGET_TYPES.M365_OVERVIEW_WIDGET),
        type: WIDGET_TYPES.M365_OVERVIEW_WIDGET,
        x: 0, y: 0, w: 8, h: 4,
        config: {},
      },
      // Row 1 Right: Service Health
      {
        i: generateWidgetId(WIDGET_TYPES.SERVICE_HEALTH_WIDGET),
        type: WIDGET_TYPES.SERVICE_HEALTH_WIDGET,
        x: 8, y: 0, w: 4, h: 4,
        config: {},
      },
      // Row 2: Azure Costs (large)
      {
        i: generateWidgetId(WIDGET_TYPES.AZURE_COST_WIDGET),
        type: WIDGET_TYPES.AZURE_COST_WIDGET,
        x: 0, y: 4, w: 6, h: 4,
        config: {},
      },
      // Row 2: Security Posture
      {
        i: generateWidgetId(WIDGET_TYPES.SECURITY_POSTURE_WIDGET),
        type: WIDGET_TYPES.SECURITY_POSTURE_WIDGET,
        x: 6, y: 4, w: 3, h: 4,
        config: {},
      },
      // Row 2: Entra ID Users
      {
        i: generateWidgetId(WIDGET_TYPES.MS_USERS_WIDGET),
        type: WIDGET_TYPES.MS_USERS_WIDGET,
        x: 9, y: 4, w: 3, h: 4,
        config: {},
      },
      // Row 3: M365 Licenses (detailed)
      {
        i: generateWidgetId(WIDGET_TYPES.MS_LICENSES_WIDGET),
        type: WIDGET_TYPES.MS_LICENSES_WIDGET,
        x: 0, y: 8, w: 6, h: 4,
        config: {},
      },
      // Row 3: Storage Usage
      {
        i: generateWidgetId(WIDGET_TYPES.STORAGE_USAGE_WIDGET),
        type: WIDGET_TYPES.STORAGE_USAGE_WIDGET,
        x: 6, y: 8, w: 4, h: 4,
        config: {},
      },
      // Row 3: Tenant Info
      {
        i: generateWidgetId(WIDGET_TYPES.TENANT_INFO_WIDGET),
        type: WIDGET_TYPES.TENANT_INFO_WIDGET,
        x: 10, y: 8, w: 2, h: 4,
        config: {},
      },
    ],
  },
  {
    id: 'executive_summary',
    name: 'Executive Summary',
    description: 'High-level KPIs and progress tracking for leadership',
    icon: 'briefcase',
    color: 'blue',
    layout: [
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 0, y: 0, w: 2, h: 2,
        config: { value: '0', label: 'Revenue', sublabel: 'This month' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 2, y: 0, w: 2, h: 2,
        config: { value: '0', label: 'Customers', sublabel: 'Total active' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 4, y: 0, w: 2, h: 2,
        config: { value: '0%', label: 'Growth', sublabel: 'Month over month' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 6, y: 0, w: 3, h: 2,
        config: { current: '0', target: '100', label: 'Q1 Goal Progress' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 9, y: 0, w: 3, h: 2,
        config: { current: '0', target: '100', label: 'Annual Target' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_TEXT),
        type: WIDGET_TYPES.CUSTOM_TEXT,
        x: 0, y: 2, w: 6, h: 2,
        config: { title: 'Key Updates', text: 'Add your important updates and announcements here.' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.QUICK_LINKS),
        type: WIDGET_TYPES.QUICK_LINKS,
        x: 6, y: 2, w: 3, h: 3,
        config: { title: 'Quick Links', links: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_COUNTDOWN),
        type: WIDGET_TYPES.CUSTOM_COUNTDOWN,
        x: 9, y: 2, w: 3, h: 2,
        config: { label: 'Next Milestone', targetDate: '' },
      },
    ],
  },
  {
    id: 'project_tracker',
    name: 'Project Tracker',
    description: 'Track projects, deadlines, and team progress',
    icon: 'folder',
    color: 'green',
    layout: [
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 0, y: 0, w: 4, h: 2,
        config: { current: '0', target: '100', label: 'Project Alpha' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 4, y: 0, w: 4, h: 2,
        config: { current: '0', target: '100', label: 'Project Beta' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_COUNTDOWN),
        type: WIDGET_TYPES.CUSTOM_COUNTDOWN,
        x: 8, y: 0, w: 4, h: 2,
        config: { label: 'Project Deadline', targetDate: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_CHECKLIST),
        type: WIDGET_TYPES.CUSTOM_CHECKLIST,
        x: 0, y: 2, w: 4, h: 4,
        config: { title: 'This Week\'s Tasks', items: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_CHECKLIST),
        type: WIDGET_TYPES.CUSTOM_CHECKLIST,
        x: 4, y: 2, w: 4, h: 4,
        config: { title: 'Blockers & Issues', items: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_NOTES),
        type: WIDGET_TYPES.CUSTOM_NOTES,
        x: 8, y: 2, w: 4, h: 4,
        config: { notes: 'Meeting notes and project updates...' },
      },
    ],
  },
  {
    id: 'personal_productivity',
    name: 'Personal Productivity',
    description: 'Stay organized with tasks, notes, and goals',
    icon: 'user',
    color: 'purple',
    layout: [
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_CHECKLIST),
        type: WIDGET_TYPES.CUSTOM_CHECKLIST,
        x: 0, y: 0, w: 4, h: 4,
        config: { title: 'Today\'s Tasks', items: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_NOTES),
        type: WIDGET_TYPES.CUSTOM_NOTES,
        x: 4, y: 0, w: 4, h: 4,
        config: { notes: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.QUICK_LINKS),
        type: WIDGET_TYPES.QUICK_LINKS,
        x: 8, y: 0, w: 4, h: 3,
        config: { title: 'Bookmarks', links: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_COUNTDOWN),
        type: WIDGET_TYPES.CUSTOM_COUNTDOWN,
        x: 8, y: 3, w: 2, h: 2,
        config: { label: 'Deadline', targetDate: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 10, y: 3, w: 2, h: 2,
        config: { value: '0', label: 'Tasks Done', sublabel: 'This week' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 0, y: 4, w: 4, h: 2,
        config: { current: '0', target: '10', label: 'Weekly Goal' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_TEXT),
        type: WIDGET_TYPES.CUSTOM_TEXT,
        x: 4, y: 4, w: 4, h: 2,
        config: { title: 'Focus Area', text: 'What\'s your main focus this week?' },
      },
    ],
  },
  {
    id: 'sales_dashboard',
    name: 'Sales Dashboard',
    description: 'Track sales metrics, pipeline, and targets',
    icon: 'dollar',
    color: 'emerald',
    layout: [
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 0, y: 0, w: 3, h: 2,
        config: { value: '$0', label: 'Revenue MTD', sublabel: 'Month to date' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 3, y: 0, w: 3, h: 2,
        config: { value: '0', label: 'Deals Closed', sublabel: 'This month' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 6, y: 0, w: 3, h: 2,
        config: { value: '$0', label: 'Pipeline Value', sublabel: 'Total opportunity' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_METRIC),
        type: WIDGET_TYPES.CUSTOM_METRIC,
        x: 9, y: 0, w: 3, h: 2,
        config: { value: '0%', label: 'Win Rate', sublabel: 'Last 30 days' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 0, y: 2, w: 6, h: 2,
        config: { current: '0', target: '100000', label: 'Monthly Target' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_PROGRESS),
        type: WIDGET_TYPES.CUSTOM_PROGRESS,
        x: 6, y: 2, w: 6, h: 2,
        config: { current: '0', target: '1000000', label: 'Annual Target' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_CHECKLIST),
        type: WIDGET_TYPES.CUSTOM_CHECKLIST,
        x: 0, y: 4, w: 4, h: 3,
        config: { title: 'Follow-ups Today', items: [] },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_TEXT),
        type: WIDGET_TYPES.CUSTOM_TEXT,
        x: 4, y: 4, w: 4, h: 3,
        config: { title: 'Top Opportunities', text: 'List your biggest deals in progress...' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.QUICK_LINKS),
        type: WIDGET_TYPES.QUICK_LINKS,
        x: 8, y: 4, w: 4, h: 3,
        config: { title: 'Sales Tools', links: [] },
      },
    ],
  },
  {
    id: 'analytics_dashboard',
    name: 'Analytics Dashboard',
    description: 'Embed reports and track key metrics with API data',
    icon: 'chart',
    color: 'orange',
    layout: [
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_API_METRIC),
        type: WIDGET_TYPES.CUSTOM_API_METRIC,
        x: 0, y: 0, w: 3, h: 2,
        config: { label: 'API Metric 1', apiUrl: '', jsonPath: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_API_METRIC),
        type: WIDGET_TYPES.CUSTOM_API_METRIC,
        x: 3, y: 0, w: 3, h: 2,
        config: { label: 'API Metric 2', apiUrl: '', jsonPath: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_API_METRIC),
        type: WIDGET_TYPES.CUSTOM_API_METRIC,
        x: 6, y: 0, w: 3, h: 2,
        config: { label: 'API Metric 3', apiUrl: '', jsonPath: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.CUSTOM_API_METRIC),
        type: WIDGET_TYPES.CUSTOM_API_METRIC,
        x: 9, y: 0, w: 3, h: 2,
        config: { label: 'API Metric 4', apiUrl: '', jsonPath: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.POWERBI_REPORT),
        type: WIDGET_TYPES.POWERBI_REPORT,
        x: 0, y: 2, w: 6, h: 4,
        config: { title: 'Report 1', embedCode: '' },
      },
      {
        i: generateWidgetId(WIDGET_TYPES.POWERBI_REPORT),
        type: WIDGET_TYPES.POWERBI_REPORT,
        x: 6, y: 2, w: 6, h: 4,
        config: { title: 'Report 2', embedCode: '' },
      },
    ],
  },
];

// Get template by ID
export const getTemplateById = (templateId) => {
  return DASHBOARD_TEMPLATES.find(t => t.id === templateId);
};

// Create a fresh layout from a template (with new widget IDs)
export const createLayoutFromTemplate = (templateId) => {
  const template = getTemplateById(templateId);
  if (!template) return [];
  
  // Generate new IDs for each widget to ensure uniqueness
  return template.layout.map(widget => ({
    ...widget,
    i: generateWidgetId(widget.type),
  }));
};
